import { Button } from 'components/button/Button';
import React from 'react';
import { navigate } from '@reach/router';
import { paths } from 'appConstants';
import { useTranslation } from 'react-i18next';
import i18n from 'i18n/i18n';
import { signOut } from 'reducers/auth';
import { useDispatch } from 'react-redux';
import {
  selectApplicationType,
  selectAssessmentId,
  selectProjectId,
} from 'reducers/virtualAssessments';
import { useSelector } from 'react-redux';
import { useIsReasonableAdjustmentAvailable } from 'hooks/useIsReasonableAdjustmentAvailable';

interface GenericFooterHeaderProps {
  step: number;
  buttonText?: string;
  buttonTextMap?: { [step: number]: string };
  incrementStep: () => void;
  permission: boolean;
  isReasonableAdjustmentBtn?: boolean;
  reasonableAdjustmentBtn?: () => void;
  redirectUrl?: string;
  redirectUrlProps?: any;
  hideNextPageButton?: boolean;
  style?: React.CSSProperties;
  extraClassName?: string;
  disabled?: boolean;
  showCheckQuestionsButton?: boolean;
  checkAnswersOnClick?: () => void;
}

const GenericActivityFooter: React.FC<GenericFooterHeaderProps> = ({
  step,
  buttonText,
  buttonTextMap,
  incrementStep,
  permission = false,
  isReasonableAdjustmentBtn,
  reasonableAdjustmentBtn,
  redirectUrl,
  redirectUrlProps,
  hideNextPageButton,
  style,
  extraClassName,
  disabled,
  showCheckQuestionsButton,
  checkAnswersOnClick,
}) => {
  const { t } = useTranslation('Generic+Activity+Footer', { i18n });
  const dispatch = useDispatch();

  const id = useSelector(selectAssessmentId);
  const applicationType = useSelector(selectApplicationType);
  const projectId = useSelector(selectProjectId);

  const isReasonableAdjustmentAvailable = useIsReasonableAdjustmentAvailable();

  const handleSuspend = () => {
    if (window.parent && window !== window.parent) {
      window.parent.postMessage({ collapseIFrame: true }, '*');
      dispatch(signOut());
    }

    if (id && applicationType && projectId) {
      navigate(`/view-application/${id}/${applicationType}/stages`, {
        state: { projectId },
      });
    } else if (redirectUrl) {
      if (
        redirectUrlProps &&
        typeof redirectUrlProps === 'object' &&
        Object.keys(redirectUrlProps).length > 0
      ) {
        navigate(redirectUrl, {
          state: { ...redirectUrlProps },
        });
      } else {
        navigate(redirectUrl);
      }
    } else {
      navigate(paths.home.url);
    }
  };

  return (
    <div
      className={`GenericActivityFooter ${
        extraClassName ? extraClassName : ''
      }`}
      style={style}
    >
      <div
        className="GenericActivityFooter__BtnWrapper WidthContent"
        style={style}
      >
        <div className="LeftHandButtons">
          <Button
            className={`${step > 2 ? 'Hidden' : ''}`}
            onClick={handleSuspend}
            priority="tertiary"
            style={style}
            disabled={disabled}
          >
            {t('SSCandidate_Generic_Activity_Footer_Suspend')}
          </Button>

          {showCheckQuestionsButton && (
            <Button
              className="Secondary Rounded"
              priority="primary"
              onClick={() => {
                if (checkAnswersOnClick) {
                  checkAnswersOnClick();
                }
              }}
            >
              Check answers
            </Button>
          )}

          {isReasonableAdjustmentBtn && isReasonableAdjustmentAvailable && (
            <Button
              className="Secondary Rounded"
              priority="tertiary"
              onClick={reasonableAdjustmentBtn}
            >
              Reasonable adjustment
            </Button>
          )}
        </div>
        <div className="RightHandButtons">
          {hideNextPageButton ? (
            ''
          ) : (
            <Button
              disabled={!permission || disabled}
              onClick={incrementStep}
              priority="primary"
              style={style}
            >
              {buttonText
                ? buttonText
                : buttonTextMap && buttonTextMap[step]
                ? buttonTextMap[step]
                : t('SSCandidate_Generic_Activity_Footer_Next_2')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export { GenericActivityFooter };
